import { useState, useEffect } from 'react';
import api from '../api';

const statusLabels = {
  sent: { label: 'Gönderildi', cls: 'bg-blue-50 text-blue-700' },
  partial: { label: 'Kısmen Teslim', cls: 'bg-orange-50 text-orange-700' },
  received: { label: 'Teslim Alındı', cls: 'bg-green-50 text-green-700' },
  cancelled: { label: 'İptal Edildi', cls: 'bg-red-50 text-red-700' },
};

function totals(byCurrency) {
  const entries = Object.entries(byCurrency || {});
  if (entries.length === 0) return '-';
  return entries.map(([cur, v]) => `${v.toFixed(2)} ${cur}`).join(' + ');
}

function StatusBadge({ status }) {
  const s = statusLabels[status] || statusLabels.sent;
  return <span className={`${s.cls} text-xs px-2 py-1 rounded font-medium`}>{s.label}</span>;
}

export default function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [showReceive, setShowReceive] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ location_id: '', receipt_date: '', note: '', quantities: {} });
  const [saving, setSaving] = useState(false);

  const fetchOrders = async () => {
    const params = filterStatus ? `?status=${filterStatus}` : '';
    const res = await api.get('/api/purchase-orders' + params);
    setOrders(res.data);
  };

  useEffect(() => { fetchOrders(); }, [filterStatus]);
  useEffect(() => { api.get('/api/locations/').then(res => setLocations(res.data)); }, []);

  const openDetail = async (id) => {
    const res = await api.get(`/api/purchase-orders/${id}`);
    setDetail(res.data);
    setShowReceive(false);
  };

  const downloadPdf = async (po) => {
    const res = await api.get(`/api/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    a.download = `${po.po_number}.pdf`;
    a.click();
  };

  const openReceive = () => {
    // Pre-fill every open line with its remaining quantity.
    const quantities = {};
    detail.lines.forEach(l => {
      const remaining = l.quantity - l.quantity_received;
      if (remaining > 0) quantities[l.id] = String(remaining);
    });
    setReceiveForm({
      location_id: locations.length === 1 ? String(locations[0].id) : '',
      receipt_date: new Date().toISOString().slice(0, 10),
      note: '',
      quantities,
    });
    setShowReceive(true);
  };

  const handleReceiveSubmit = async (e) => {
    e.preventDefault();
    const lines = Object.entries(receiveForm.quantities)
      .map(([id, qty]) => ({ po_line_id: parseInt(id), quantity: parseFloat(qty) || 0 }))
      .filter(l => l.quantity > 0);
    setSaving(true);
    try {
      const res = await api.post(`/api/purchase-orders/${detail.id}/receive`, {
        location_id: parseInt(receiveForm.location_id),
        receipt_date: receiveForm.receipt_date,
        note: receiveForm.note || null,
        lines,
      });
      setDetail(res.data);
      setShowReceive(false);
      fetchOrders();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt(`${detail.po_number} siparişi iptal edilecek. Teslim alınmış miktarlar stokta kalır.\n\nİptal nedeni (isteğe bağlı):`);
    if (reason === null) return;
    setSaving(true);
    try {
      const res = await api.post(`/api/purchase-orders/${detail.id}/cancel`, { reason: reason || null });
      setDetail(res.data);
      fetchOrders();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? orders.filter(o => o.po_number.toLowerCase().includes(q) || (o.supplier || '').toLowerCase().includes(q) || (o.project_name || '').toLowerCase().includes(q))
    : orders;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Satın Alma Siparişleri</h2>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 PO no, tedarikçi veya proje ara..."
          className="border rounded px-3 py-2 text-sm w-72"
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">Tüm Durumlar</option>
          <option value="sent">Gönderildi</option>
          <option value="partial">Kısmen Teslim</option>
          <option value="received">Teslim Alındı</option>
          <option value="cancelled">İptal Edildi</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">{filtered.length} sipariş</span>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">PO No</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Tarih</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Tedarikçi</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Proje</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Kalem</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tutar</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Durum</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(o => (
              <tr key={o.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-mono text-gray-700">{o.po_number}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{o.created_at ? o.created_at.slice(0, 10) : '-'}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{o.supplier}</td>
                <td className="px-4 py-3 text-gray-600">{o.project_name || '-'}</td>
                <td className="px-4 py-3 text-right text-gray-600">{o.line_count}</td>
                <td className="px-4 py-3 text-right text-gray-800">{totals(o.total_by_currency)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openDetail(o.id)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-3">Detay / Teslim Al</button>
                  <button onClick={() => downloadPdf(o)} className="text-gray-500 hover:text-gray-700 font-medium text-sm">PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {orders.length === 0 ? 'Henüz satın alma siparişi yok. Tedarik sayfasından PO gönderildiğinde burada saklanır.' : 'Aramayla eşleşen sipariş bulunamadı'}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold">{detail.po_number}</h3>
                <p className="text-sm text-gray-500">
                  {detail.supplier}{detail.project_name ? ` · ${detail.project_name}` : ''} · {detail.created_at ? detail.created_at.slice(0, 10) : ''}{detail.sent_to ? ` · ${detail.sent_to}` : ''}
                </p>
              </div>
              <StatusBadge status={detail.status} />
            </div>

            {detail.status === 'cancelled' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                {detail.cancelled_at ? detail.cancelled_at.slice(0, 10) : ''} tarihinde{detail.cancelled_by ? ` ${detail.cancelled_by} tarafından` : ''} iptal edildi{detail.cancel_reason ? `: ${detail.cancel_reason}` : ''}
              </div>
            )}

            {showReceive ? (
              <form onSubmit={handleReceiveSubmit}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Depo *</label>
                    <select required value={receiveForm.location_id} onChange={e => setReceiveForm({...receiveForm, location_id: e.target.value})} className="w-full border rounded px-3 py-2">
                      <option value="">Seçiniz</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teslim Tarihi *</label>
                    <input required type="date" value={receiveForm.receipt_date} onChange={e => setReceiveForm({...receiveForm, receipt_date: e.target.value})} className="w-full border rounded px-3 py-2" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                    <input value={receiveForm.note} onChange={e => setReceiveForm({...receiveForm, note: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="İrsaliye no, kargo..." />
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-500">Ürün</th>
                      <th className="text-right px-3 py-2 text-gray-500">Sipariş</th>
                      <th className="text-right px-3 py-2 text-gray-500">Teslim Alınan</th>
                      <th className="text-right px-3 py-2 text-gray-500 w-32">Şimdi Alınan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detail.lines.map(l => {
                      const remaining = l.quantity - l.quantity_received;
                      return (
                        <tr key={l.id}>
                          <td className="px-3 py-2"><div className="font-medium text-gray-800">{l.product_name}</div><div className="text-xs font-mono text-gray-500">{l.product_code}</div></td>
                          <td className="px-3 py-2 text-right text-gray-700">{l.quantity} {l.unit}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{l.quantity_received}</td>
                          <td className="px-3 py-2">
                            {remaining > 0 ? (
                              <input type="number" step="any" min="0" max={remaining} value={receiveForm.quantities[l.id] ?? ''} onChange={e => setReceiveForm({...receiveForm, quantities: {...receiveForm.quantities, [l.id]: e.target.value}})} className="w-full border rounded px-2 py-1 text-right" />
                            ) : <span className="text-green-600 text-xs">Tamamlandı</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">Alınan miktarlar seçilen depoya stok girişi olarak eklenir.</p>
                <div className="flex gap-3 mt-6">
                  <button type="submit" disabled={saving} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-medium disabled:opacity-50">{saving ? 'Kaydediliyor...' : 'Teslim Al ve Stoğa Ekle'}</button>
                  <button type="button" onClick={() => setShowReceive(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
                </div>
              </form>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-500">Ürün</th>
                      <th className="text-right px-3 py-2 text-gray-500">Miktar</th>
                      <th className="text-right px-3 py-2 text-gray-500">Birim Fiyat</th>
                      <th className="text-right px-3 py-2 text-gray-500">Teslim Alınan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detail.lines.map(l => (
                      <tr key={l.id}>
                        <td className="px-3 py-2"><div className="font-medium text-gray-800">{l.product_name}</div><div className="text-xs font-mono text-gray-500">{l.product_code}</div></td>
                        <td className="px-3 py-2 text-right text-gray-700">{l.quantity} {l.unit}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{l.unit_price != null ? `${l.unit_price.toFixed(4)} ${l.currency || ''}` : '-'}</td>
                        <td className={`px-3 py-2 text-right ${l.quantity_received >= l.quantity ? 'text-green-600' : 'text-gray-700'}`}>{l.quantity_received} / {l.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {detail.receipts.length > 0 && (
                  <div className="mt-5">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Teslim Alma Geçmişi</h4>
                    <div className="space-y-2">
                      {detail.receipts.map(r => (
                        <div key={r.id} className="bg-gray-50 rounded p-3 text-sm">
                          <div className="text-gray-700 font-medium">{r.receipt_date} · {r.location_name}{r.note ? ` · ${r.note}` : ''}</div>
                          <div className="text-gray-500 text-xs mt-1">{r.lines.map(l => `${l.product_code} × ${l.quantity}`).join(', ')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  {detail.status !== 'received' && detail.status !== 'cancelled' && (
                    <>
                      <button onClick={openReceive} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-medium">📥 Teslim Al</button>
                      <button onClick={handleCancel} disabled={saving} className="flex-1 bg-red-50 text-red-700 py-2 rounded-lg hover:bg-red-100 font-medium disabled:opacity-50">✕ Siparişi İptal Et</button>
                    </>
                  )}
                  <button onClick={() => downloadPdf(detail)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">PDF İndir</button>
                  <button onClick={() => setDetail(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">Kapat</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
